import {
  DecisionPageLayout,
  DecisionSection,
  DecisionSubHeading,
  DecisionList,
  InlineRelatedLinks,
} from "@/components/DecisionPageLayout";
import { Link } from "@/lib/router-compat";

const seo = {
  title: "What TMS Feels Like | Neuro Wellness TMS Centers of America",
  description:
    "What does TMS therapy feel like? Learn what to expect during a TMS session, including common sensations and side effects.",
  canonical: "/what-tms-feels-like",
  keywords:
    "what does TMS feel like, TMS sensation, TMS experience, TMS side effects, TMS tapping sensation",
};

const relatedLinks = [
  { title: "What Is TMS?", href: "/contents/tms/what-is-tms-therapy" },
  {
    title: "Typical TMS Treatment",
    href: "/contents/tms/typical-tms-treatment",
  },
  {
    title: "Is TMS Right for Me?",
    href: "/contents/tms/is-tms-right-for-me",
  },
  {
    title: "What to Expect From Your Consultation",
    href: "/what-to-expect-from-your-consultation",
  },
];

export default function WhatTmsFeelsLike() {
  return (
    <DecisionPageLayout
      seo={seo}
      badge="Patient Experience"
      title="What TMS Feels Like"
      subtitle="Most of the anxiety about TMS comes from not knowing what it feels like. Here's a direct, honest description."
    >
      <DecisionSection heading="During the session">
        <p>
          You sit in a comfortable chair, like a dentist's chair but more
          reclined. A technician places a magnetic coil against your head, near
          the left side of your forehead. When the stimulation begins, you feel
          a tapping sensation on your scalp — not painful, but noticeable. Some
          people describe it as like a woodpecker tapping against your head.
        </p>
        <p>
          Each pulse lasts a fraction of a second. The tapping comes in rapid
          bursts, then pauses, then repeats. A session typically runs about 20
          to 40 minutes for standard TMS. You're awake, alert, and can talk,
          watch TV, or listen to music throughout.
        </p>
      </DecisionSection>

      <DecisionSection heading="Common sensations">
        <DecisionList
          items={[
            "Tapping or knocking sensation on the scalp — the most common description",
            "Scalp discomfort or mild headache, especially in the first few sessions",
            "A twitching sensation in the face or jaw muscles near the coil",
            "A loud clicking sound from the coil — earplugs are provided",
          ]}
        />
      </DecisionSection>

      <DecisionSection heading="What it doesn't feel like">
        <p>
          TMS is not ECT. There's no anesthesia, no seizure, no memory loss, no
          confusion afterward. You're fully conscious and can drive yourself
          home. It's not a shock — it's a magnetic pulse, similar in strength to
          an MRI scanner but focused on a much smaller area.
        </p>
      </DecisionSection>

      <DecisionSection heading="After the session">
        <p>
          Most people feel completely normal immediately after a session. Scalp
          discomfort or mild headache, if it occurs, usually fades within an
          hour or two. Over-the-counter pain relievers can help if needed. Side
          effects typically decrease after the first few sessions as your body
          adjusts.
        </p>
      </DecisionSection>

      <DecisionSection heading="SAINT feels the same — but faster">
        <p>
          SAINT sessions use the same type of magnetic stimulation, but each
          session is shorter (about 10 minutes) and you have ten per day. The
          tapping sensation is the same. The main difference is the schedule,
          not the sensation.
        </p>
      </DecisionSection>

      <DecisionSection heading="Your next step">
        <p>
          Call{" "}
          <a
            href="tel:9548279793"
            className="text-accent font-semibold hover:underline"
          >
            (954) 827-9793
          </a>{" "}
          or{" "}
          <Link to="/contactus" className="text-accent hover:underline">
            schedule a consultation
          </Link>
          .
        </p>
      </DecisionSection>

      <InlineRelatedLinks heading="Related Pages" links={relatedLinks} />
    </DecisionPageLayout>
  );
}
